import React, {ClipboardEventHandler, CSSProperties, FocusEventHandler, forwardRef, useEffect, useImperativeHandle, useRef} from 'react';
import './TextField.less';
import {stripHtml} from 'string-strip-html';
import DOMPurify from 'dompurify';
import {sanitizeRichPaste} from '@/lib/sanitizeRichPaste';

/**
 * Static function, used throughout the user interface.
 */
const createMarkup = (value: string): { __html: string } => {
    return {__html: DOMPurify.sanitize(value)};
};

/**
 * Strip html formatting and return a stripped string.
 */
const stripHTML = (input: string, ignoreTags?: string[]): string => {
    if (!input) return '';
    else {
        const firstPass = stripHtml(input, {
            stripTogetherWithTheirContents: ["script", "style", "xml", "class"],
            ignoreTags: ignoreTags ?? ['div', 'p', 'br', 'em', 'strong', 'b', 'i', 'strong', 'u', 'ul', 'ol', 'li', 'hr', 'img', 'a', 'h1', 'h2', 'h3', 'h4'],
            skipHtmlDecoding: true
        }).result ?? '';

        const stripped = firstPass
            .replaceAll(/(<[^>]+) class=".*?"/ig, '$1')
            .replaceAll(/(<[^>]+) style=".*?"/ig, '$1')
            .replaceAll(/(<[^>]+) style='.*?'/ig, '$1')
            .replaceAll(/(<[^>]+) href=".*?"/ig, '$1')
            .replaceAll(/(<[^>]+) href='.*?'/ig, '$1')
            .replaceAll(/·[\s?]+[&nbsp;]+/ig, '&nbsp;&nbsp;&bull; ')
            .replaceAll(/([0-9]+)\.[\s?]+[&nbsp;]+/g, '&nbsp;&nbsp;$1. ');

        return stripped;
    }
};

/**
 * Convert plain clipboard text to minimal HTML for a rich paste: HTML-escape
 * and turn newlines into <br> so pasted plain text keeps its line breaks.
 */
const plainTextToHtml = (text: string): string => {
    if (!text) return '';
    const esc = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    return esc.split(/\r\n|\r|\n/).join('<br>');
};

const renderHTML = (input: string, stripHtmlFlag?: boolean, additionalClassName?: string) => {
    const textOnlyClass = stripHtmlFlag ? 'plain-text' : '';
    const className = additionalClassName || '';
    const text = stripHtmlFlag ? stripHTML(input) : input;

    return <div key={Math.random()}
                className={`text-field ${className} ${textOnlyClass}`.trim()}
                style={{display: 'inline-block !important'}}
                dangerouslySetInnerHTML={createMarkup(text)}
    />;
};

/**
 * Latest callback props, delivered to the memoized inner component through a
 * stable ref. The memo comparator below deliberately ignores `onChange`/
 * `onBlur` so parent re-renders don't recreate the contenteditable (which
 * would reset the caret). But skipping the re-render also freezes the handler
 * props at their last-rendered values — for a function-component parent those
 * closures capture stale state, so a keystroke would write into an old clone
 * of the entity and wipe every field set since (OWA-516: typing the expense
 * Description erased the already-picked Type and Paid-to). The unmemoized
 * outer wrapper updates this ref on every parent render, so the inner
 * component always dispatches to the freshest closures.
 */
type LatestCallbacks = {
    onChange?: TextFieldProps['onChange'],
    onBlur?: TextFieldProps['onBlur'],
    data?: any,
};

type TextFieldInnerProps = TextFieldProps & {
    latestRef: React.MutableRefObject<LatestCallbacks>,
};

const TextFieldInnerFC = forwardRef<HTMLDivElement, TextFieldInnerProps>((props, ref) => {
    const contentRef = useRef<HTMLDivElement>(null);

    // Expose the inner div via the forwarded ref so callers can keep using ref.
    useImperativeHandle(ref, () => contentRef.current as HTMLDivElement, []);

    /**
     * Check for autoFocus. If set, select the contents.
     */
    useEffect(() => {
        if (props.autoFocus && contentRef.current) {
            contentRef.current.focus();

            const cell = contentRef.current;
            let range: Range, selection: Selection;
            if (window.getSelection) {
                selection = window.getSelection();
                range = document.createRange();
                range.selectNodeContents(cell);
                selection.removeAllRanges();
                selection.addRange(range);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Copied from the 'net. Move caret to end of contenteditable.
     */
    const setEndOfContentEditable = (contentEditableElement: HTMLElement) => {
        let range: Range, selection: Selection;
        if (document.createRange) {
            range = document.createRange();
            range.selectNodeContents(contentEditableElement);
            range.collapse(false);
            selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
        }
    };

    /**
     * We do the stripping in blur to not mess everything up
     */
    const onBlur: FocusEventHandler<HTMLDivElement> = (e) => {
        const latest = props.latestRef.current;
        if (latest.onBlur) {
            latest.onBlur(e, latest.data, contentRef.current.innerHTML, contentRef.current.innerText);
        }
    };

    const onChange = (e: any) => {
        const latest = props.latestRef.current;
        if (latest.onChange) {
            latest.onChange(e, latest.data, e.target.innerHTML ?? '', e.target.innerText ?? '');
        }
    };

    /**
     * When focused, select the text in the div.
     */
    const onFocus: FocusEventHandler<HTMLDivElement> = () => {
        setEndOfContentEditable(contentRef.current);
    };

    /**
     * Called when pasting data from an external source.
     * This will strip all the html, leaving just text with minimal formatting.
     */
    const onPaste: ClipboardEventHandler<HTMLDivElement> = (e) => {
        // Cancel paste
        e.preventDefault();

        const plain = e.clipboardData.getData('text/plain');
        const html = e.clipboardData.getData('text/html');

        const selection = window.getSelection();
        if (!selection.rangeCount) return;

        const range = selection.getRangeAt(0);
        range.deleteContents();

        if (props.richPaste) {
            // Rich paste: keep basic formatting + line breaks, drop Word/HTML
            // cruft (see sanitizeRichPaste). Plain-only clipboards keep their
            // newlines via <br>.
            const cleanHtml = html ? sanitizeRichPaste(html) : plainTextToHtml(plain);
            const temp = document.createElement('div');
            temp.innerHTML = cleanHtml;
            const frag = document.createDocumentFragment();
            let node: ChildNode | null;
            let lastNode: ChildNode | null = null;
            while ((node = temp.firstChild)) {
                lastNode = frag.appendChild(node);
            }
            range.insertNode(frag);

            // Move the caret to the end of the inserted content
            if (lastNode) {
                range.setStartAfter(lastNode);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
            }
        }
        else {
            // Plain paste: insert clipboard text with no markup.
            const text = stripHTML(html);
            const toPaste = props.stripHtml ? plain : text || plain;
            range.insertNode(document.createTextNode(toPaste));

            // Move the caret to the end of the pasted text
            selection.collapseToEnd();
        }

        // Notify the parent with the FULL updated content of the contentEditable —
        // not just the pasted clipboard payload. Mirroring the onInput path above
        // (innerHTML + innerText) keeps cached/serialized state consistent with
        // what the user actually sees. (OWA-389)
        const div = contentRef.current;
        const latest = props.latestRef.current;
        if (latest.onChange && div) {
            latest.onChange(e, latest.data, div.innerHTML ?? '', div.innerText ?? '');
        }
    };

    const disabledClass = props.disabled ? 'disabled' : '';
    // A rich-paste field keeps formatting, so it must NOT get the plain-text
    // class (which forces color:black / font-size:1em).
    const textOnlyClass = (props.stripHtml && !props.richPaste) ? 'plain-text' : '';
    const className = props.className || '';
    const requiredClass = props.required ? 'required' : '';
    // Rich-paste fields preserve entered whitespace (newlines/runs of spaces).
    const richClass = props.richPaste ? 'rich-text' : '';

    return <div id={props.id}
                key={props.id || props.data || props.defaultValue}
                className={`text-field component ${className} ${disabledClass} ${textOnlyClass} ${requiredClass} ${richClass}`.trim()}
                ref={contentRef}
                style={props.styles}
                contentEditable={props.isEditable ?? true}
                suppressContentEditableWarning={true}
                onBlur={onBlur}
                onInput={onChange}
                dangerouslySetInnerHTML={createMarkup(props.defaultValue)}
                draggable={props.draggable}
                onFocus={onFocus}
                onPaste={(props.stripHtml || props.richPaste) ? onPaste : null}
                data-private={props.encrypted}
    />;
});
TextFieldInnerFC.displayName = 'TextFieldInner';

/**
 * We only update if one of the non-text-related fields changes.
 * (Matches the original shouldComponentUpdate logic — but as a memo comparator.)
 *
 * `onChange`/`onBlur`/`data` are intentionally NOT compared — a parent passing
 * fresh inline closures every render must not force the contenteditable to
 * re-render (that resets the caret mid-typing). The inner component instead
 * dispatches handlers through `latestRef`, which the outer wrapper refreshes
 * on every parent render (see LatestCallbacks above).
 */
const areEqual = (prev: TextFieldInnerProps, next: TextFieldInnerProps): boolean => {
    return !(
        next.isEditable !== prev.isEditable ||
        next.disabled !== prev.disabled ||
        next.forceRefreshCounter !== prev.forceRefreshCounter ||
        next.autoFocus !== prev.autoFocus ||
        next.stripHtml !== prev.stripHtml ||
        next.richPaste !== prev.richPaste ||
        next.draggable !== prev.draggable ||
        next.data !== prev.data
    );
};

const TextFieldInner = React.memo(TextFieldInnerFC, areEqual);

/**
 * Unmemoized outer wrapper: refreshes `latestRef` with the newest callback
 * props on every parent render, then renders the memoized inner component.
 * The memo still suppresses DOM re-renders (caret safety); the ref makes sure
 * the suppressed renders can't freeze stale `onChange`/`onBlur` closures.
 */
const TextFieldFC = forwardRef<HTMLDivElement, TextFieldProps>((props, ref) => {
    const latestRef = useRef<LatestCallbacks>({});
    latestRef.current = {onChange: props.onChange, onBlur: props.onBlur, data: props.data};

    return <TextFieldInner {...props} latestRef={latestRef} ref={ref}/>;
});
TextFieldFC.displayName = 'TextField';

const TextField = TextFieldFC as typeof TextFieldFC & {
    createMarkup: typeof createMarkup;
    stripHTML: typeof stripHTML;
    renderHTML: typeof renderHTML;
};

TextField.createMarkup = createMarkup;
TextField.stripHTML = stripHTML;
TextField.renderHTML = renderHTML;

export type TextFieldProps = {
    // Should this component get focus
    autoFocus?: boolean,

    // Additional class names to append
    className?: string,

    // The default value for the component
    defaultValue?: any,

    // Data to pass back
    data?: any,

    // Is the field disabled?
    disabled?: boolean,

    // Can this element be dragged?
    draggable?: boolean,

    // Should the data
    encrypted?: boolean,

    // A total hack to deal with how fucking hard it is to determine when to redraw
    forceRefreshCounter?: number,

    // ID for the field
    id?: string,

    // Is the component currently editable?
    isEditable?: boolean,

    // Function to fire when the input field is blurred
    onBlur?: (e: React.FocusEvent<HTMLDivElement>, data: any, htmlText: string, rawText: string) => void,

    // Optional onChange event
    onChange?: (e: any, data: any, value: string, rawValue?: string) => void,

    // Is this field required?
    required?: boolean,

    // Should contents be stripped of formatting?
    stripHtml?: boolean,

    // On paste, sanitize HTML to a safe formatting allow-list (bold/italic/
    // underline, lists, links, line breaks) and drop Word/HTML cruft, instead
    // of flattening to plain text. Use for rich-text fields (e.g. invoice
    // description). See sanitizeRichPaste.
    richPaste?: boolean,

    // Additional styles
    styles?: CSSProperties,
};

export default TextField;
